import { useState } from 'react';
import styled from '@emotion/styled';
import { Button, Modal, Stack, tokens, Alert } from '@hotjar/hotsauce';
import type {
  PlanId,
  Allowance,
  EnhancedProduct,
  ProductInformation,
  PlanName,
} from '@hotjar/product-catalog';
import { useProductCatalogRepo } from '@hotjar/product-catalog';
import { useTranslation } from '@hotjar/translations';
import type { GlobalModalProps } from '../external-dependencies/webapp-utils';
import { createGlobalModal } from '../external-dependencies/webapp-utils';
import { PlanFeatures } from './components/plan-features/PlanFeatures';
import { PlanRadioCard } from './components/plan-selection/radio-card/PlanRadioCard';
import { CONTACT_SALES, PRICING_MODEL_CHANGES_URL } from './constants';

interface Props {
  product: EnhancedProduct;
  billingPeriod: 'MONTHLY' | 'ANNUALLY';
  currency: 'EUR' | 'USD';
  currentPlan: {
    id: PlanId;
    name: PlanName;
    planTier: number;
    allowance: Allowance;
  };
  currentSubscriptionPlan: {
    id: PlanId;
    name: PlanName;
    planTier: number;
    allowance: Allowance;
  };
  subscriptionEndDate?: string;
  showActionRequiredAlert: boolean;
}

const S = {
  Modal: styled(Modal)`
    width: 72vw;
    min-width: 915px;
  `,
  ModalBody: styled(Modal.Body)`
    background-color: ${tokens.BRAND_COLORS.cobalt_100};
    padding: ${tokens.spacer.x3} ${tokens.spacer.x1pt5};
  `,
  ModalFooter: styled(Modal.Footer)`
    gap: ${tokens.spacer.x2};
    align-items: center;
  `,
  Stack: styled(Stack)`
    align-items: stretch;
    & > * {
      flex: 1;
    }
  `,
  Alert: styled(Alert)`
    width: fit-content;
    & p {
      margin-top: 0;
    }
  `,
};

const FORM_ID = 'plan-selection';

export function PlanSelectionModal({
  onSubmit,
  onCancel,
  product,
  currentPlan,
  currentSubscriptionPlan,
  billingPeriod,
  currency,
  subscriptionEndDate,
  showActionRequiredAlert,
}: Props & GlobalModalProps<ProductInformation>): JSX.Element {
  const { t: tProductInformation } = useTranslation('checkout', {
    keyPrefix: 'product-information.product',
  });
  const { t: tPlanSelectionModal } = useTranslation('checkout', {
    keyPrefix: 'wizard.plan-selection-modal',
  });
  const { t: tActionRequired } = useTranslation('checkout', {
    keyPrefix: 'wizard.subscriptions.action-required.alert',
  });
  const { t: tSubscriptions } = useTranslation('checkout', { keyPrefix: 'wizard.subscriptions' });
  const { getProductBySku } = useProductCatalogRepo({ billingPeriod, currency });
  const [selectedProduct, setSelectedProduct] = useState<ProductInformation>();

  const submitPlan = () => {
    if (selectedProduct !== undefined) {
      onSubmit(selectedProduct);
    }
  };

  const shouldShowBookACall = (selectedProduct?.plan.planTier ?? 0) > 2;

  const isSelectedSelfServe = selectedProduct?.allowance.selfServeEnabled === true;

  const isSelectedPriceLower =
    (selectedProduct?.allowance.price ?? 0) < currentSubscriptionPlan.allowance.price;

  const isSelectedTierHigher =
    (selectedProduct?.plan.planTier ?? 0) > currentSubscriptionPlan.planTier;

  const shouldContactSupport =
    selectedProduct != null &&
    (!isSelectedSelfServe || (isSelectedPriceLower && isSelectedTierHigher));

  return (
    <S.Modal
      appElementSelector="#root"
      isOpen
      contentLabel={tPlanSelectionModal('content-label')}
      onClose={onCancel}
    >
      <Modal.Header
        title={tPlanSelectionModal('select-your-plan', {
          product: tProductInformation(product.name),
        })}
        subtitle={tPlanSelectionModal(`subtitle-${product.name}`)}
      />
      <S.ModalBody>
        <Stack spacing="x3" direction="column">
          {showActionRequiredAlert && (
            <Alert
              kind="warning"
              title={tActionRequired('title')}
              actionText={tActionRequired('action')}
              actionHref={PRICING_MODEL_CHANGES_URL}
              actionProps={{ isExternal: true }}
            >
              {tActionRequired('description', { subscriptionEndDate })}
            </Alert>
          )}
          <form onSubmit={submitPlan} id={FORM_ID}>
            <S.Stack spacing="x1pt5">
              {product?.plans.map((plan) => (
                <div key={plan.id}>
                  <PlanRadioCard
                    plan={plan}
                    isSelected={plan.allowances.some(
                      (allowance) => allowance.sku === selectedProduct?.allowance.sku,
                    )}
                    onSelect={(sku) => setSelectedProduct(getProductBySku(sku))}
                    currency={currency}
                    billingPeriod={billingPeriod}
                    current={{
                      isCurrentPlan: currentPlan.id === plan.id,
                      allowance: currentPlan.allowance,
                    }}
                    key={plan.id}
                  />
                  <PlanFeatures product={product} plan={plan} />
                </div>
              ))}
            </S.Stack>
          </form>
        </Stack>
      </S.ModalBody>
      <S.ModalFooter>
        {shouldContactSupport && (
          <>
            <S.Alert>{tPlanSelectionModal('self-serve-message')}</S.Alert>
            <Button as="a" href={CONTACT_SALES} isExternal type="submit" kind="primary">
              {tSubscriptions('book-a-call')}
            </Button>
          </>
        )}
        {!shouldContactSupport && (
          <>
            {shouldShowBookACall && isSelectedSelfServe && (
              <Button as="a" href={CONTACT_SALES} isExternal type="submit" kind="secondary">
                {tSubscriptions('book-a-call')}
              </Button>
            )}
            <Button isDisabled={selectedProduct === undefined} type="submit" form={FORM_ID}>
              {tSubscriptions('change-plan')}
            </Button>
          </>
        )}
      </S.ModalFooter>
    </S.Modal>
  );
}

export const openPlanSelectionModal = createGlobalModal<Props, ProductInformation, Error>(
  PlanSelectionModal,
);
